/**
 * Backup Health & Exclusion Management (Spec 211 — US1/US2/US4/US5, T023)
 *
 * The Backup section's "Health" tab: two views over the backup platform's
 * governance layer —
 *   • Problems — every asset (lakehouse / warehouse / workspace-DR) with no
 *     successful backup, each tagged with a reason category + readable detail,
 *     with per-row remediation (Exclude / Repoint / Retry).
 *   • Exclusions — the active managed-out entities (the single exclusion source),
 *     each re-includable.
 *
 * Built entirely from @/components/ui + design tokens + lucide (Principle XLV).
 * All numbers/actions come from the backend contract — no client-side recompute.
 *
 * Author: Peter Dobler (Dobler Data Solutions)
 * Feature: 211-backup-health-exclusion-management
 */

import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Tabs,
  DataTable,
  Badge,
  Button,
  Modal,
  Select,
  Input,
  Textarea,
  EmptyState,
  LoadingState,
  ErrorState,
  AppTooltip,
} from '@/components/ui';
import {
  ShieldOff,
  ShieldCheck,
  Ban,
  HardDriveDownload,
  RefreshCw,
  RotateCcw,
  Copy,
  Terminal,
} from 'lucide-react';
import {
  backupHealthApi,
  BackupProblem,
  BackupExclusion,
  BackupHealthEntityType,
  ReasonCategory,
  StoragePrepCommand,
} from '../../api/backupHealth';
import { backupReadinessApi } from '../../api/backupReadiness'; // Spec 236
import { ReadinessView } from './ReadinessView'; // Spec 236 — Readiness tab
import { assignmentApi } from '../../api/assignment';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../../../hooks/useToast';
import { ListFilterBar, textMatch } from '../common/ListFilterBar';
import { formatEastern } from '@/utils/dateFormatter';
import { storageAccountLabel } from '../../utils/storageAccountLabel';

type HealthView = 'problems' | 'readiness' | 'exclusions';

// ── Reason-category presentation (tokenized Badges) ─────────────────────────
const REASON_META: Record<ReasonCategory, { label: string; variant: 'default' | 'warning' | 'danger' | 'info' | 'success' }> = {
  storage_unreachable: { label: 'Storage unreachable', variant: 'danger' },
  oom: { label: 'Out of memory', variant: 'danger' },
  sp_forbidden: { label: 'SP forbidden', variant: 'danger' },
  empty: { label: 'Empty', variant: 'default' },
  timeout: { label: 'Timeout', variant: 'warning' },
  schema_error: { label: 'Schema error', variant: 'warning' },
  config_error: { label: 'Config error', variant: 'warning' },
  unknown: { label: 'Unknown', variant: 'info' },
  // entity-state reasons (derived) — an unprotected entity that hasn't (yet) failed
  never_run: { label: 'Never run', variant: 'warning' },
  in_progress: { label: 'In progress', variant: 'info' },
  no_recovery_point: { label: 'No recovery point', variant: 'warning' },
  unsupported: { label: 'Unsupported items', variant: 'default' },
  capture_failed: { label: 'Capture failed', variant: 'danger' },
  operator: { label: 'Operator', variant: 'info' },
  // Spec 214 FR-007: a run settled by the exclusion flow — visible after re-include
  // (previously fell through to an "Unknown" badge).
  excluded: { label: 'Excluded (settled)', variant: 'default' },
};

/** Reasons an operator can pick when manually excluding (Spec 214 D7: ONE vocabulary with the
 *  API — everything offered here is accepted server-side; only pure-state/display categories
 *  are withheld: 'in_progress' is transient, 'unknown' isn't a deliberate governance reason,
 *  'excluded' is display-only). */
const NON_EXCLUDE_REASONS: ReasonCategory[] = ['unknown', 'in_progress', 'excluded'];

const ENTITY_META: Record<BackupHealthEntityType, { label: string; variant: 'info' | 'success' | 'warning' }> = {
  lakehouse: { label: 'Lakehouse', variant: 'info' },
  warehouse: { label: 'Warehouse', variant: 'success' },
  workspace: { label: 'Workspace-DR', variant: 'warning' },
};

const REASON_OPTIONS: { value: ReasonCategory; label: string }[] = (Object.keys(REASON_META) as ReasonCategory[])
  .filter((k) => !NON_EXCLUDE_REASONS.includes(k)) // diagnosis/state outputs are not operator exclude choices
  .map((k) => ({ value: k, label: REASON_META[k].label }));

const ReasonBadge: React.FC<{ reason: ReasonCategory }> = ({ reason }) => {
  const m = REASON_META[reason] ?? REASON_META.unknown;
  return <Badge variant={m.variant}>{m.label}</Badge>;
};

/** A reason an operator may pick in the Exclude modal (and the API accepts). */
const isExcludableReason = (r: string): r is ReasonCategory =>
  r in REASON_META && !NON_EXCLUDE_REASONS.includes(r as ReasonCategory);

/** Spec 236 T044: a predicted problem carries its readiness code's category ('recoverability', 'access', …). */
const readinessCategoryLabel = (c: string): string =>
  REASON_META[c as ReasonCategory]?.label ?? (c ? c.charAt(0).toUpperCase() + c.slice(1) : 'Readiness');

const ProblemReason: React.FC<{ problem: BackupProblem }> = ({ problem }) =>
  problem.phase === 'predicted' ? (
    <span className="flex flex-wrap items-center gap-1">
      <AppTooltip title="The readiness check expects the next backup of this asset to fail. No backup has failed yet.">
        <Badge variant="info">Predicted</Badge>
      </AppTooltip>
      <Badge variant="default">{readinessCategoryLabel(problem.reasonCategory)}</Badge>
    </span>
  ) : (
    <ReasonBadge reason={problem.reasonCategory} />
  );

const EntityBadge: React.FC<{ entityType: BackupHealthEntityType }> = ({ entityType }) => {
  const m = ENTITY_META[entityType] ?? { label: entityType, variant: 'info' as const };
  return <Badge variant={m.variant}>{m.label}</Badge>;
};

const fmtWhen = (iso: string | null): string => (iso ? formatEastern(iso) : '—');

// ════════════════════════════════════════════════════════════════════════════
// Exclude modal — required reason category + note → POST /exclusions
// ════════════════════════════════════════════════════════════════════════════
const ExcludeModal: React.FC<{
  target: BackupProblem | null;
  onClose: () => void;
  onExcluded: () => void;
}> = ({ target, onClose, onExcluded }) => {
  const toast = useToast();
  const [reason, setReason] = useState<ReasonCategory>('operator');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  // Reset the form each time a new target opens the modal.
  React.useEffect(() => {
    if (target) {
      // A predicted problem's readiness category is not an exclusion reason; fall back to Operator.
      setReason(isExcludableReason(target.reasonCategory) ? target.reasonCategory : 'operator');
      setNote('');
    }
  }, [target]);

  const noteMissing = note.trim().length === 0;

  const submit = async () => {
    if (!target || noteMissing) return;
    setBusy(true);
    try {
      await backupHealthApi.exclude({
        entityType: target.entityType,
        entityId: target.entityId,
        reasonCategory: reason,
        note: note.trim(),
      });
      toast.success(`Excluded ${target.displayName} from backup.`);
      onExcluded();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to exclude entity.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen={!!target}
      onClose={onClose}
      title="Exclude from backup"
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="danger" onClick={submit} disabled={busy || noteMissing}>
            <Ban className="h-4 w-4" /> Exclude
          </Button>
        </div>
      }
    >
      {target && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-subtle)] px-3 py-2">
            <EntityBadge entityType={target.entityType} />
            <span className="font-medium text-[var(--color-text-primary)]">{target.displayName}</span>
          </div>
          <p className="text-sm text-[var(--color-text-secondary)]">
            Excluding removes this entity from the coverage denominator and stops it being attempted — its
            schedules are deactivated and the materializer won&apos;t re-enroll it. Reversible via re-include.
          </p>
          <Select
            label="Reason category"
            value={reason}
            onChange={(e) => setReason(e.target.value as ReasonCategory)}
            options={REASON_OPTIONS}
          />
          <Textarea
            label="Note (required)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Why is this entity being excluded? (e.g. dev lakehouse — backed up by the dev job)"
            rows={3}
            state={noteMissing ? 'error' : 'default'}
            errorMessage={noteMissing ? 'A note is required.' : undefined}
          />
        </div>
      )}
    </Modal>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// Repoint modal — pick a reachable storage account → POST /repoint
// ════════════════════════════════════════════════════════════════════════════
const RepointModal: React.FC<{
  target: BackupProblem | null;
  onClose: () => void;
  onRepointed: () => void;
}> = ({ target, onClose, onRepointed }) => {
  const toast = useToast();
  const [storageAccountId, setStorageAccountId] = useState('');
  const [busy, setBusy] = useState(false);
  const [prep, setPrep] = useState<StoragePrepCommand | null>(null);

  const accountsQ = useQuery({
    queryKey: ['backup', 'storage-accounts'],
    queryFn: assignmentApi.storageAccounts,
    enabled: !!target,
  });

  React.useEffect(() => {
    if (target) {
      setStorageAccountId('');
      setPrep(null);
    }
  }, [target]);

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Command copied — paste it into Azure Cloud Shell.');
    } catch {
      toast.error('Copy failed — select the command and copy it manually.');
    }
  };

  const accountOptions = useMemo(
    () => [
      { value: '', label: 'Select a reachable account…' },
      ...(accountsQ.data ?? []).map((a) => {
        // Identity first, description beside it (owner rule) — so the list sorts and scans by the
        // account name, which is what the portal, the private endpoints and the DNS records show.
        const base = storageAccountLabel(a);
        return { value: a.id, label: a.envHint ? `${base} (${a.envHint})` : base };
      }),
    ],
    [accountsQ.data]
  );

  const submit = async () => {
    if (!target || !storageAccountId) return;
    setBusy(true);
    setPrep(null);
    try {
      const res = await backupHealthApi.repoint(target.entityType, target.entityId, storageAccountId);
      // `prepared:true` = a Prepare-destination step is NEEDED (grant missing) — NOT success.
      if (res.prepared) {
        // Fetch + show the exact grant command; the app can't self-grant (needs an Azure admin).
        try {
          setPrep(await backupHealthApi.prepCommand(storageAccountId));
        } catch {
          setPrep(null);
        }
        toast.warning(
          `Grant the backup service principal access to ${res.accountName ?? 'the destination'} first — command below.`
        );
      } else if (res.reason) {
        // Refusal (workspace storage is the plan's) or not-found — surface it, don't claim success.
        toast.warning(res.reason);
      } else {
        toast.success(`Repointed ${target.displayName} — next backup targets ${res.accountName ?? 'the new account'}.`);
        onRepointed();
        onClose();
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to repoint storage.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen={!!target}
      onClose={onClose}
      title="Repoint backup storage"
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} disabled={busy || !storageAccountId}>
            <HardDriveDownload className="h-4 w-4" /> Repoint
          </Button>
        </div>
      }
    >
      {target && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-subtle)] px-3 py-2">
            <EntityBadge entityType={target.entityType} />
            <span className="font-medium text-[var(--color-text-primary)]">{target.displayName}</span>
          </div>
          <p className="text-sm text-[var(--color-text-secondary)]">
            Move this entity&apos;s primary backup storage to a reachable account. The next backup targets the
            new account (reuses the Spec-208 destination-reassignment path).
          </p>
          {accountsQ.isLoading ? (
            <LoadingState rows={2} />
          ) : (
            <Select
              label="Destination account"
              value={storageAccountId}
              onChange={(e) => setStorageAccountId(e.target.value)}
              options={accountOptions}
            />
          )}
          {prep && (
            <div className="space-y-2 rounded-md border border-[var(--color-status-warning)] bg-[var(--color-bg-subtle)] px-3 py-3 text-sm text-[var(--color-text-secondary)]">
              <p className="font-medium text-[var(--color-text-primary)]">Prepare &ldquo;{prep.accountName}&rdquo; first</p>
              <p>
                The app can&apos;t grant storage access itself — that needs an Azure admin. Run this once in
                Azure Cloud Shell (it&apos;s already signed in as you), then click Repoint again.
              </p>
              {prep.minimalRbacCommand && (
                <div className="relative">
                  <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded border border-[var(--color-border)] bg-[var(--color-bg-card)] p-2 pr-9 text-xs text-[var(--color-text-primary)]">
                    {prep.minimalRbacCommand}
                  </pre>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1 !px-1.5 !py-1"
                    onClick={() => copy(prep.minimalRbacCommand!)}
                    title="Copy command"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-3 pt-0.5">
                <a
                  href={prep.cloudShellUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-[var(--color-accent)] hover:underline"
                >
                  <Terminal className="h-3.5 w-3.5" /> Open Azure Cloud Shell
                </a>
              </div>
              <details className="text-xs">
                <summary className="cursor-pointer text-[var(--color-text-secondary)]">
                  Firewalled (default-deny) destination? Also run the private-endpoint blueprint
                </summary>
                <div className="relative mt-1">
                  <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded border border-[var(--color-border)] bg-[var(--color-bg-card)] p-2 pr-9 text-[var(--color-text-primary)]">
                    {prep.fullBlueprintCommand}
                  </pre>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1 !px-1.5 !py-1"
                    onClick={() => copy(prep.fullBlueprintCommand)}
                    title="Copy command"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </details>
              {prep.loginHint && <p className="text-xs opacity-80">{prep.loginHint}</p>}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// Problems view
// ════════════════════════════════════════════════════════════════════════════
const ProblemsView: React.FC = () => {
  const qc = useQueryClient();
  const toast = useToast();
  const { isOperatorOrAbove } = useAuth();
  const canManage = isOperatorOrAbove();

  const problemsQ = useQuery({ queryKey: ['backup', 'health', 'problems'], queryFn: backupHealthApi.problems });

  const [q, setQ] = useState('');
  const [reasonFilter, setReasonFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [excludeTarget, setExcludeTarget] = useState<BackupProblem | null>(null);
  const [repointTarget, setRepointTarget] = useState<BackupProblem | null>(null);
  const [retryingKey, setRetryingKey] = useState<string | null>(null);

  const rows = problemsQ.data ?? [];
  const filtered = useMemo(
    () =>
      rows.filter(
        (p) =>
          (!reasonFilter || p.reasonCategory === reasonFilter) &&
          (!typeFilter || p.entityType === typeFilter) &&
          textMatch(q, p.displayName, p.entityId, p.detail)
      ),
    [rows, q, reasonFilter, typeFilter]
  );

  const refetchAll = () => {
    void problemsQ.refetch();
    void qc.invalidateQueries({ queryKey: ['backup', 'health', 'exclusions'] });
    void qc.invalidateQueries({ queryKey: ['backup', 'dash'] });
  };

  const onRetry = async (p: BackupProblem) => {
    const key = `${p.entityType}:${p.entityId}`;
    setRetryingKey(key);
    try {
      // Spec 214 FR-006: the honest contract — a refusal (already running / no storage /
      // at cap) is NOT a success; a launch shows its real execution reference.
      const res = await backupHealthApi.retry(p.entityType, p.entityId);
      if (res.launched) {
        toast.success(`Retry launched for ${p.displayName}${res.executionName ? ` (${res.executionName})` : ''}.`);
      } else {
        toast.error(`Retry refused for ${p.displayName}: ${res.reason ?? 'unknown reason'}.`);
      }
      refetchAll();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to launch retry.');
    } finally {
      setRetryingKey(null);
    }
  };

  if (problemsQ.isLoading) return <LoadingState rows={6} />;
  if (problemsQ.isError) return <ErrorState onRetry={() => problemsQ.refetch()} />;
  if (rows.length === 0) {
    return (
      <EmptyState
        icon={<ShieldCheck className="h-10 w-10 text-emerald-600 dark:text-emerald-400" />}
        title="No backup problems"
        message="Every backup-capable entity has a successful backup or is explicitly excluded — nothing to diagnose."
      />
    );
  }

  return (
    <>
      <ListFilterBar query={q} onQuery={setQ} placeholder="Search entities…" count={filtered.length} total={rows.length}>
        <Select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          options={[
            { value: '', label: 'All asset classes' },
            { value: 'lakehouse', label: 'Lakehouse' },
            { value: 'warehouse', label: 'Warehouse' },
            { value: 'workspace', label: 'Workspace-DR' },
          ]}
        />
        <Select
          value={reasonFilter}
          onChange={(e) => setReasonFilter(e.target.value)}
          options={[
            { value: '', label: 'All reasons' },
            ...(Object.keys(REASON_META) as ReasonCategory[]).map((k) => ({ value: k, label: REASON_META[k].label })),
          ]}
        />
      </ListFilterBar>
      <Card padding="none">
        <DataTable<BackupProblem>
          minWidth="64rem"
          columns={[
            {
              key: 'displayName',
              header: 'Asset',
              sortable: true,
              render: (p) => (
                <span className="flex items-center gap-2">
                  <EntityBadge entityType={p.entityType} />
                  <span className="truncate font-medium text-[var(--color-text-primary)]">{p.displayName}</span>
                </span>
              ),
            },
            {
              key: 'reasonCategory',
              header: 'Reason',
              width: '12rem',
              sortable: true,
              sortValue: (p) => p.reasonCategory,
              render: (p) => <ProblemReason problem={p} />,
            },
            { key: 'detail', header: 'Detail', render: (p) => p.detail || '—' },
            {
              key: 'lastSeen',
              header: 'Last seen',
              width: '12rem',
              sortValue: (p) => p.lastSeen ?? '',
              render: (p) => fmtWhen(p.lastSeen),
            },
            {
              key: 'actions',
              header: '',
              width: '17rem',
              align: 'right',
              sortable: false,
              render: (p) => {
                const key = `${p.entityType}:${p.entityId}`;
                if (!canManage) return null;
                return (
                  <span className="flex justify-end gap-1.5">
                    <Button variant="outline" size="sm" onClick={() => setExcludeTarget(p)} title="Exclude from backup">
                      <Ban className="h-4 w-4" /> Exclude
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setRepointTarget(p)} title="Repoint backup storage">
                      <HardDriveDownload className="h-4 w-4" /> Repoint
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onRetry(p)}
                      disabled={retryingKey === key}
                      title="Retry backup"
                    >
                      <RefreshCw className={retryingKey === key ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} /> Retry
                    </Button>
                  </span>
                );
              },
            },
          ]}
          rows={filtered}
          rowKey={(p) => `${p.entityType}:${p.entityId}`}
        />
      </Card>

      <ExcludeModal target={excludeTarget} onClose={() => setExcludeTarget(null)} onExcluded={refetchAll} />
      <RepointModal target={repointTarget} onClose={() => setRepointTarget(null)} onRepointed={refetchAll} />
    </>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// Exclusions view
// ════════════════════════════════════════════════════════════════════════════
const ExclusionsView: React.FC = () => {
  const qc = useQueryClient();
  const toast = useToast();
  const { isOperatorOrAbove } = useAuth();
  const canManage = isOperatorOrAbove();

  const exclusionsQ = useQuery({ queryKey: ['backup', 'health', 'exclusions'], queryFn: backupHealthApi.exclusions });

  const [q, setQ] = useState('');
  const [reincludingId, setReincludingId] = useState<string | null>(null);

  const rows = exclusionsQ.data ?? [];
  const filtered = useMemo(
    () => rows.filter((e) => textMatch(q, e.displayName, e.entityId, e.note, e.excludedBy)),
    [rows, q]
  );

  const onReinclude = async (e: BackupExclusion) => {
    setReincludingId(e.id);
    try {
      await backupHealthApi.reinclude(e.id);
      toast.success(`Re-included ${e.displayName} — it re-enrolls on the next materialize.`);
      void exclusionsQ.refetch();
      void qc.invalidateQueries({ queryKey: ['backup', 'health', 'problems'] });
      void qc.invalidateQueries({ queryKey: ['backup', 'dash'] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to re-include entity.');
    } finally {
      setReincludingId(null);
    }
  };

  if (exclusionsQ.isLoading) return <LoadingState rows={5} />;
  if (exclusionsQ.isError) return <ErrorState onRetry={() => exclusionsQ.refetch()} />;
  if (rows.length === 0) {
    return (
      <EmptyState
        icon={<ShieldOff className="h-10 w-10 text-[var(--color-text-secondary)]" />}
        title="No active exclusions"
        message="Nothing is managed out of backup coverage right now. Exclude a problematic entity from the Problems tab."
      />
    );
  }

  return (
    <>
      <ListFilterBar query={q} onQuery={setQ} placeholder="Search exclusions…" count={filtered.length} total={rows.length} />
      <Card padding="none">
        <DataTable<BackupExclusion>
          minWidth="64rem"
          columns={[
            {
              key: 'displayName',
              header: 'Asset',
              sortable: true,
              render: (e) => (
                <span className="flex items-center gap-2">
                  <EntityBadge entityType={e.entityType} />
                  <span className="truncate font-medium text-[var(--color-text-primary)]">{e.displayName}</span>
                </span>
              ),
            },
            {
              key: 'reasonCategory',
              header: 'Reason',
              width: '12rem',
              sortValue: (e) => e.reasonCategory,
              render: (e) => <ReasonBadge reason={e.reasonCategory} />,
            },
            { key: 'note', header: 'Note', render: (e) => e.note || '—' },
            { key: 'excludedBy', header: 'By', width: '13rem', render: (e) => e.excludedBy ?? '—' },
            {
              key: 'excludedAt',
              header: 'When',
              width: '12rem',
              sortValue: (e) => e.excludedAt,
              render: (e) => fmtWhen(e.excludedAt),
            },
            {
              key: 'actions',
              header: '',
              width: '9rem',
              align: 'right',
              sortable: false,
              render: (e) =>
                canManage ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onReinclude(e)}
                    disabled={reincludingId === e.id}
                    title="Re-include (restore normal enrollment)"
                  >
                    <RotateCcw className={reincludingId === e.id ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} /> Re-include
                  </Button>
                ) : null,
            },
          ]}
          rows={filtered}
          rowKey={(e) => e.id}
        />
      </Card>
    </>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// Backup Health area (two views)
// ════════════════════════════════════════════════════════════════════════════
export const BackupHealthArea: React.FC = () => {
  const [view, setView] = useState<HealthView>('problems');
  const exclusionsQ = useQuery({ queryKey: ['backup', 'health', 'exclusions'], queryFn: backupHealthApi.exclusions });
  const problemsQ = useQuery({ queryKey: ['backup', 'health', 'problems'], queryFn: backupHealthApi.problems });
  // Spec 236: predicted-failure count drives the Readiness tab badge.
  const readinessSummaryQ = useQuery({
    queryKey: ['backup', 'health', 'readiness', 'summary'],
    queryFn: backupReadinessApi.summary,
  });

  return (
    <div className="space-y-4">
      <Tabs
        tabs={[
          { key: 'problems', label: 'Problems', count: problemsQ.data?.length },
          { key: 'readiness', label: 'Readiness', count: readinessSummaryQ.data?.willFail || undefined },
          { key: 'exclusions', label: 'Exclusions', count: exclusionsQ.data?.length },
        ]}
        value={view}
        onChange={(k) => setView(k as HealthView)}
      />
      {view === 'problems' ? <ProblemsView /> : view === 'readiness' ? <ReadinessView /> : <ExclusionsView />}
    </div>
  );
};

export default BackupHealthArea;
